import * as React from 'react';

import {Accessibility} from '@dnd-kit/dom';
import type {
  DragStartEvent as DomDragStartEvent,
  DragOverEvent as DomDragOverEvent,
  DragEndEvent as DomDragEndEvent,
} from '@dnd-kit/dom';

import type {TranslationFunction} from 'src/types/language-support';


type DragEntity =
  | DomDragStartEvent['operation']['source']
  | DomDragOverEvent['operation']['target'];

/**
 * Resolve the droppable id from an `@dnd-kit/dom` drag entity. Sortables
 * carry their group via `entity.sortable.group`; plain droppables /
 * draggables fall back to `entity.id`.
 *
 * Duck-typed on `.sortable.group` rather than `isSortable()` because
 * `isSortable` has overloads that don't accept a `Draggable | Droppable`
 * union — and source vs target arguments use different concrete types
 * even though the property we read (`.sortable.group`) is structurally
 * identical on both.
 */
function resolveDroppableId(entity: DragEntity): string {
  if (!entity) {
    return '';
  }
  const maybeSortable = (entity as {sortable?: {group?: unknown}}).sortable;
  if (maybeSortable?.group != null) {
    return String(maybeSortable.group);
  }
  return String(entity.id);
}

/**
 * Return the screen-reader label for the dragged entity. Sources tag their
 * data with `{label: string}` (the widget's display name); we fall back to
 * the localized "tile" if missing so the announcement still reads cleanly.
 */
function resolveDragSourceLabel(
  source: DomDragStartEvent['operation']['source'],
  t: TranslationFunction,
): string {
  if (!source) {
    return t('tile', 'tile');
  }
  const data = source.data as {label?: string} | undefined;
  return data?.label ?? t('tile', 'tile');
}

/**
 * Configures `@dnd-kit/dom`'s `Accessibility` plugin with translated
 * announcements for screen-reader users at every drag lifecycle stage,
 * plus the keyboard-instructions hint emitted on draggable focus.
 *
 * `describeDroppable` translates a droppable id (row id, new-row band id)
 * into a human-readable position label like "KPI row 2" or "new chart or
 * table row" — provided by the canvas because it owns the dashboard
 * structure.
 */
export function useAccessibilityPlugin(
  describeDroppable: (droppableId: string) => string,
  t: TranslationFunction,
) {
  return React.useMemo(
    () =>
      Accessibility.configure({
        announcements: {
          dragstart(event: DomDragStartEvent) {
            const src = event.operation.source;
            if (!src) {
              return undefined;
            }
            const label = resolveDragSourceLabel(src, t);
            const droppableId = resolveDroppableId(src);
            return t('Picked_up_tile_from_source', {
              tile: label,
              source: describeDroppable(droppableId),
              defaultValue: `Picked up ${label} from ${describeDroppable(
                droppableId,
              )}`,
            });
          },
          dragover(event: DomDragOverEvent) {
            const target = event.operation.target;
            if (!target) {
              return undefined;
            }
            const droppableId = resolveDroppableId(target);
            return t('Over_destination', {
              destination: describeDroppable(droppableId),
              defaultValue: `Over ${describeDroppable(droppableId)}`,
            });
          },
          dragend(event: DomDragEndEvent) {
            const src = event.operation.source;
            if (!src) {
              return undefined;
            }
            const label = resolveDragSourceLabel(src, t);
            if (event.canceled) {
              return t(
                'Drag_cancelled_tile_returned_to_its_original_position',
                {
                  tile: label,
                  defaultValue: `Drag cancelled. ${label} returned to its original position`,
                },
              );
            }
            const target = event.operation.target;
            if (!target) {
              return t('Dropped_tile', {
                tile: label,
                defaultValue: `Dropped ${label}`,
              });
            }
            const droppableId = resolveDroppableId(target);
            return t('Dropped_tile_in_destination', {
              tile: label,
              destination: describeDroppable(droppableId),
              defaultValue: `Dropped ${label} in ${describeDroppable(
                droppableId,
              )}`,
            });
          },
        },
        screenReaderInstructions: {
          draggable: t(
            'Press_Enter_or_Space_to_start_dragging_Use_arrow_keys_to_move_Press_Enter_or_Space_again_to_drop',
            'Press Enter or Space to start dragging. Use arrow keys to move. Press Enter or Space again to drop.',
          ),
        },
      }),
    [describeDroppable, t],
  );
}
